import { collection, addDoc, getDocs, query, doc, getDoc, setDoc, Firestore } from "firebase/firestore";
import { FIREBASE_FIRESTORE, FIREBASE_DEFAULT_FIRESTORE, DEFAULT_DB_AVAILABLE } from "../Firebase";
import { getRemoteEnv } from '../config/remoteEnv';

// Collections that live in the default Firestore DB after migration.
// For apps not yet migrated, these will be empty in the default DB and
// the fallback logic will transparently read from the named DB instead.
const DEFAULT_DB_COLLECTIONS = new Set([
    "storeConfig",
    "storeConfigCache",
    "users",
    "tokens",
    "admin_data",
    "system",
    "otpCodes",
    "restaurants",
    "deliveryQuotes",
    "mailingList",
    "orders",
    "orders_test",
    "orderBatches",
    "orderTestBatches",
]);

function isMigratedCollection(collectionName: string): boolean {
    return DEFAULT_DB_COLLECTIONS.has(collectionName);
}

/**
 * Returns the status document ID for a collection.
 *
 * Default DB collections use {appId}_status to avoid collision across apps.
 * Named DB collections use the legacy "status".
 */
function getStatusDocId(collectionName: string, useDefault: boolean): string {
    if (useDefault) {
        const appId = getRemoteEnv().appId;
        return appId ? `${appId}_status` : "status";
    }
    return "status";
}


/**
 * Returns true for sentinel/operational documents that should be excluded
 * from collection scan results.
 *
 * Named DB:    "status"
 * Default DB:  "{appId}_status", "{appId}_metadata"
 *
 * NOTE: "{appId}_properties" is intentionally NOT filtered — it is
 * legitimate config data handled by isPropertiesDoc() in config.ts.
 */
function isSentinelDoc(docId: string): boolean {
    return (
        docId === "status" ||
        docId.endsWith("_status") ||
        docId.endsWith("_metadata")
    );
}

/**
 * Performs the actual Firestore read against a given DB instance.
 * Separated out so both the default DB and named DB fallback can use it.
 */
async function fetchFromDb(
    db: Firestore,
    collection_name: string,
    document?: string,
    queryObj?: any
): Promise<any> {
    const collectionRef = collection(db, collection_name);

    if (queryObj) {
        const q      = query(collectionRef, queryObj);
        const docRef = await getDocs(q);
        return docRef.docs
            .filter((x) => !isSentinelDoc(x.id))
            .map((x) => ({ id: x.id, ...x.data() }));
    }

    if (!document) {
        const docRef = await getDocs(collectionRef);
        const data   = docRef.docs
            .filter((x) => !isSentinelDoc(x.id))
            .map((x) => ({ id: x.id, ...x.data() }));

        if (collection_name === 'storeConfigCache') {
            console.log(JSON.stringify(data));
        }
        return data;
    }

    const ref    = doc(db, collection_name, document);
    const docRef = await getDoc(ref);
    const data   = docRef.data();

    if (collection_name === 'storeConfigCache') {
        console.log(JSON.stringify(data));
    }
    return data;
}

/**
 * Returns true if a fetch result is considered empty.
 * Used to decide whether to fall back to the named DB.
 */
function isEmpty(data: any): boolean {
    if (data === undefined || data === null) return true;
    if (Array.isArray(data) && data.length === 0) return true;
    return false;
}

class DataService {

    /**
     * Reads data from Firestore with local caching.
     *
     * For migrated collections (storeConfig, storeConfigCache, {appId}_users):
     *   1. Tries the default DB first (migrated apps)
     *   2. Falls back to the named DB if default DB returns empty (unmigrated apps)
     *
     * For all other collections:
     *   Always reads from the named DB.
     *
     * Once all apps are migrated the fallback can be removed.
     */
    public static GetData = async (
        collection_name: string,
        document?: string,
        queryObj?: any,
        setObj?: any,
        notCache?: boolean
    ) => {
        console.log("DATA SERVICE.GET_DATA CALLED: ", collection_name, document, queryObj);

        // A pool without a default database reads the named DB only.
        const migrated = isMigratedCollection(collection_name) && (await DEFAULT_DB_AVAILABLE());
        // Cache keys MUST be scoped per app: every /r/{app} brand shares this
        // origin's localStorage, and unscoped keys (e.g. the whole
        // storeConfigCache read) served one brand's config — logo, name,
        // theme — to another after cross-brand navigation.
        let cacheScope = "noapp";
        try { cacheScope = getRemoteEnv().appId || "noapp"; } catch { /* pre-config */ }
        const asyncKey = cacheScope + ":" + collection_name + "_" + document + "_" + JSON.stringify(queryObj);

        // ── Cache check ────────────────────────────────────────────────────────
        if (!notCache) {
            const localData = await localStorage.getItem(asyncKey);
            if (localData) {
                const parsedLocalData = JSON.parse(localData);
                if (setObj) {
                    console.log("calling setObj to set local object...");
                    setObj(parsedLocalData);
                }

                // Use the appropriate DB and status doc ID for the cache
                // freshness check — we'll do a lightweight status doc read
                // against whichever DB actually has the data. We don't know
                // yet which DB has the data, so check default first and fall
                // back to named if not found.
                let statusDb      = migrated ? FIREBASE_DEFAULT_FIRESTORE() : FIREBASE_FIRESTORE();
                let statusDocId   = getStatusDocId(collection_name, migrated);
                let statusRef     = doc(statusDb, collection_name, statusDocId);
                let status        = await getDoc(statusRef);

                // If status doc not found in default DB, try named DB
                if (!status.exists() && migrated) {
                    statusDb    = FIREBASE_FIRESTORE();
                    statusDocId = getStatusDocId(collection_name, false);
                    statusRef   = doc(statusDb, collection_name, statusDocId);
                    status      = await getDoc(statusRef);
                }

                if (status.exists()) {
                    console.log("status exists for", collection_name);
                    const lastUpdate    = status.data().updateTime;
                    const lastRetrieval = parsedLocalData.retrievalTime;

                    console.log("Last update was: ",    lastUpdate);
                    console.log("Last retrieval was: ", lastRetrieval);

                    if (new Date(lastUpdate) < new Date(lastRetrieval)) {
                        console.log("Update not required!", collection_name);
                        return parsedLocalData.data;
                    }
                }
            }
        }

        // ── Fetch from Firestore ───────────────────────────────────────────────
        console.log("Getting data from server...", collection_name, document);

        let data: any;

        if (migrated) {
            // Try default DB first (migrated apps)
            data = await fetchFromDb(FIREBASE_DEFAULT_FIRESTORE(), collection_name, document, queryObj);

            if (isEmpty(data)) {
                // Default DB is empty — app not yet migrated, fall back to named DB
                console.warn(
                    `[DataService] ${collection_name} empty in default DB — ` +
                    `falling back to named DB (app not yet migrated)`
                );
                data = await fetchFromDb(FIREBASE_FIRESTORE(), collection_name, document, queryObj);
            } else {
                console.log(`[DataService] ${collection_name} served from default DB`);
            }
        } else {
            // Non-migrated collection — always use named DB
            data = await fetchFromDb(FIREBASE_FIRESTORE(), collection_name, document, queryObj);
        }

        // ── Cache result ───────────────────────────────────────────────────────
        const currentTime = (new Date()).toUTCString();
        localStorage.setItem(asyncKey, JSON.stringify({ retrievalTime: currentTime, data }));

        return data;
    }


    public static addData = async (
        collection_name: string,
        data: any,
        metadata?: any,
        document_name?: string
    ) => {
        try {
            if (metadata) {
                data["metadata"] = metadata;
            }

            const migrated    = isMigratedCollection(collection_name) && (await DEFAULT_DB_AVAILABLE());
            const db          = migrated ? FIREBASE_DEFAULT_FIRESTORE() : FIREBASE_FIRESTORE();
            const statusDocId = getStatusDocId(collection_name, migrated);

            const collectionRef = collection(db, collection_name);
            const statusRef     = doc(db, collection_name, statusDocId);
            const currentTime   = (new Date()).toUTCString();

            await setDoc(statusRef, { updateTime: currentTime });

            if (!document_name) {
                const docRef = await addDoc(collectionRef, data);
                console.log("Document written: ", docRef.id);
            } else {
                const ref = doc(collectionRef, document_name);
                await setDoc(ref, data);
                console.log("Document written: ", document_name);
            }
        } catch (error) {
            console.log("Error: ", error);
        }

        return;
    }
}

export default DataService;